// import qs from 'qs';
import jwtDecode from 'jwt-decode';
import Cookies from 'js-cookie';
import moment from 'moment';
import { IdentityServerAPI, PoliciesAPI, LogoutApi } from '@services/api';
import {
  USER_ACCESS_COOKIE,
  SUPER_USER_ACCESS_COOKIE,
  USER_TYPE,
  FIDELIZE_BROKER_FEATURES_COOKIE,
} from '@plataforma/utils';
import { clearAuthData } from './clearAuthData';

const LOCAL_STORAGE_KEYS = {
  accessToken: 'access_token',
  expiresIn: 'expires_in',
  createdAt: 'create_at',
  username: 'username',
  broker: 'broker'
};

export const getAuth = () => Cookies.getJSON(USER_ACCESS_COOKIE) || {};

export const isTokenExpired = (expiresIn, createAt) => {
  const expirationDate = moment(new Date(createAt)).add(expiresIn, `milliseconds`);
  const difference = moment().diff(expirationDate);
  const isValid = difference > 1;
  return isValid;
};

export const isAuthenticated = () => {
  const { token, broker, refreshExpiresIn, expiresIn, createAt } = getAuth();
  const tokenLifeSpan = refreshExpiresIn || expiresIn;
  const user = broker && broker.user;
  return user && token && !isTokenExpired(tokenLifeSpan, createAt);
};

export const getItem = key =>
  key ? localStorage.getItem(LOCAL_STORAGE_KEYS[key]) : null;
export const setItem = (key, value) =>
  key ? localStorage.setItem(LOCAL_STORAGE_KEYS[key], value) : null;

export const getSuperUser = () =>
  Cookies.getJSON(SUPER_USER_ACCESS_COOKIE) || null;

export const isSuperUserLogged = () => {
  const superUser = getSuperUser();

  if (!superUser) {
    return false;
  }

  return !isTokenExpired(superUser.token.expires_in, superUser.token.create_at);
};

export const getUseRefreshToken = () => {
  const { useRefreshToken } = Cookies.getJSON(USER_ACCESS_COOKIE) || {};
  return useRefreshToken;
};

export const getUserProfile = () => {
  let userProfile;

  const { token } = Cookies.getJSON(USER_ACCESS_COOKIE) || {};
  const useRefreshToken = getUseRefreshToken();
  const decodedToken = token && jwtDecode(token);

  const isSuperUser = decodedToken && decodedToken.loginOwnerUserName;
  const isUserBroker =
    decodedToken && useRefreshToken
      ? decodedToken.realm_access.roles.includes('broker')
      : decodedToken && decodedToken.role === 'broker';

  if (isSuperUser) {
    userProfile = USER_TYPE.commercial;
  } else if (isUserBroker) {
    userProfile = USER_TYPE.broker;
  } else {
    userProfile = USER_TYPE.policyholder;
  }

  return userProfile;
};

/**
 * logout()
 *
 * Clear the website data and redirect user to the login page
 * or the theme page if applicable
 */
export const logout = () => {
  const { refreshToken } = Cookies.getJSON(USER_ACCESS_COOKIE);
  Promise.all([LogoutApi.logoutUser(refreshToken), clearAuthData()]).then(
    () => {
      const userTheme = localStorage.getItem('userTheme');

      window.location = `${process.env.REACT_APP_PLATAFORMA_LOGIN}${
        userTheme ? `/${userTheme}` : ''
      }`;
    }
  );
};

export const getPermissions = async () => {
  const { data } = await IdentityServerAPI.RefreshToken();

  const permissions =
    data.claims['permissions.policyholder'] !== undefined
      ? data.claims['permissions.policyholder']
      : [];

  const modalities =
    data.claims['modalities.policyholder'] !== undefined
      ? data.claims['modalities.policyholder']
      : [];

  return { permissions, modalities };
};

/**
 * changeBroker()
 *
 * Clear the website data but keeps the super user data and redirect user to
 * the broker selection page
 */
export const changeBroker = async () => {
  await clearAuthData(true);

  window.location = `${process.env.REACT_APP_PLATAFORMA_LOGIN_COMERCIAL}/select-broker`;
};

/**
 * logoutSuperUser()
 *
 * Clear the website data and redirects user to the commercial user's login page
 */
 export const logoutSuperUser = async () => {
  await clearAuthData();

  window.location = `${process.env.REACT_APP_PLATAFORMA_LOGIN_COMERCIAL}`;
};

class Authentication {
  constructor(urlOrigin, urlLogin) {
    this.urlOrigin = urlOrigin;
    this.urlLogin = urlLogin;
  }

  async handleAuthentication() {
    const isUserAuthenticated = isAuthenticated();
    if (isUserAuthenticated) {
      this.handleSaveData();
      return isUserAuthenticated;
    }

    this.redirectWithReturnUrl();
  }

  handleSaveData() {
    // const { search, pathname } = window.location;
    // const params = qs.parse(search, { ignoreQueryPrefix: true });
    const auth = getAuth();
    // const stringifiedParams = qs.stringify(params, { addQueryPrefix: true });

    if (auth && Object.keys(auth).length > 0) {
      const username =
        auth.broker && auth.broker.user && auth.broker.user.userName;
      // persist in localstorage auth data from login
      this.saveAuthData({ username: { username }, broker: auth.broker });
      // window.history.replaceState({}, document.title, `${pathname}${stringifiedParams}`);
    }
  }

  saveAuthData({ username, broker }) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.username, JSON.stringify(username));
    localStorage.setItem(LOCAL_STORAGE_KEYS.broker, JSON.stringify(broker));
  }

  updateCookie(data, susepValidateData) {
    const authToken = Cookies.getJSON(USER_ACCESS_COOKIE) || {};
    const theme = localStorage.getItem('userTheme');
    const permissions =
      data.claims['permissions.policyholder'] !== undefined
        ? data.claims['permissions.policyholder']
        : [];
    const modalities =
      data.claims['modalities.policyholder'] !== undefined
        ? data.claims['modalities.policyholder']
        : [];

    const access = {
      ...authToken,
      token: data.access_token,
      refreshToken: data.refresh_token,
      tokenType: data.token_type,
      createAt: new Date(),
      useRefreshToken: data.use_refresh_token || false,
      expiresIn: data.expires_in * 1000, // data.expires_in comes in seconds
      refreshExpiresIn: data.refresh_expires_in * 1000 || null, // data.refresh_expires_in comes in seconds
      userId: (data && data.userId) || null,
      email: data.email,
      username: data.username,
      userTheme: theme,
      modalities,
      permissions,
      isSusepValidated: susepValidateData.registered || false
    };

    const millisecondsToAddToCookieTime =
      access.refreshExpiresIn || access.expiresIn;
    const dateExpire = new Date(
      new Date().getTime() + millisecondsToAddToCookieTime
    );

    Cookies.set(USER_ACCESS_COOKIE, access, {
      expires: dateExpire,
      domain: `${process.env.REACT_APP_DOMAIN}`
    });

    // update fidelize features cookie with same expiry of user access
    Cookies.set(
      FIDELIZE_BROKER_FEATURES_COOKIE,
      Cookies.getJSON(FIDELIZE_BROKER_FEATURES_COOKIE),
      {
        expires: dateExpire,
        domain: `${process.env.REACT_APP_DOMAIN}`
      }
    );
  }

  getUserData() {
    return getAuth();
  }

  async getBroker() {
    const { broker } = getAuth();
    if (broker && Object.keys(broker).length > 1) return broker;

    this.redirectWithReturnUrl();
    return null;
  }

  getRedirectUrl() {
    const urlBase = this.urlLogin;
    const url = `${urlBase}?redirectUrl=${
      this.urlOrigin ? encodeURI(this.urlOrigin) : ''
    }`;
    return url;
  }

  redirectWithReturnUrl() {
    window.location = this.getRedirectUrl();
  }

  async refreshToken() {
    const { data } = await IdentityServerAPI.RefreshToken();
    const {
      data: susepValidateData
    } = await PoliciesAPI.getRegisterValidationSusep();

    return data && this.updateCookie(data, susepValidateData);
  }

  static getAccessToken() {
    const { token } = getAuth();
    return token;
  }

  setHideIncentiveTermsModal(value) {
    const userCookie = getAuth();
    const millisecondsToAddToCookieTime =
    userCookie.refreshExpiresIn || userCookie.expiresIn;
    const dateExpire = new Date(
      new Date().getTime() + millisecondsToAddToCookieTime
    );
    const data = {
      ...userCookie,
      bonusJourney: {
        ...userCookie.bonusJourney,
        hideIncentiveTermsModal: value
      },
    };
    Cookies.set(USER_ACCESS_COOKIE, data, {
      expires: dateExpire,
      domain: `${process.env.REACT_APP_DOMAIN}`
    });
  }
}

export default Authentication;
